// APPLE_MPP_REASON is the EmailReasonStatus value the email service writes for
// Apple Mail Privacy Protection opens (mirrors botfilter.DeviceTagAppleMPP in
// the email repo). Apple's relay fetches the tracking pixel at delivery, so an
// MPP open can't be confirmed as a human read — the timeline renders it with a
// weaker "may have been opened" affordance via OPENED_APPLE_MPP_V2 below.
export const APPLE_MPP_REASON = 'apple_mpp';

// APPLE_MPP_OPENED_STATUS is the synthetic status the email component
// substitutes for "opened" when an open is Apple-MPP-qualified, so it maps to
// the softened OPENED_APPLE_MPP_V2 title instead of the confirmed OPENED_V2.
export const APPLE_MPP_OPENED_STATUS = 'opened_apple_mpp';

// APPLE_MPP_TOOLTIP_KEY is the plain-language hover shown on the warning icon
// next to an MPP-qualified open, explaining why it can't be confirmed without
// putting jargon in the inline status label.
export const APPLE_MPP_TOOLTIP_KEY = 'ACTIVITY.EMAIL_TITLE.APPLE_MPP_TOOLTIP';

// isAppleMppOpen reports whether an open should be softened to the
// "possibly opened" affordance: a confirmed "opened" status carrying the
// Apple-MPP reason tag the email service writes. Pure (no Angular deps) so it
// can be unit-tested without a component harness.
export function isAppleMppOpen(status: string | undefined, statusReason: string | undefined): boolean {
  return status?.toLowerCase().trim() === 'opened' && statusReason === APPLE_MPP_REASON;
}

export const EMAIL_TITLE_KEYS_V2: Record<string, string> = {
  bounced: 'ACTIVITY.EMAIL_TITLE.BOUNCED_V2',
  clicked: 'ACTIVITY.EMAIL_TITLE.CLICKED_V2',
  deferred: 'ACTIVITY.EMAIL_TITLE.DEFERRED_V2',
  delivered: 'ACTIVITY.EMAIL_TITLE.DELIVERED_V2',
  dropped: 'ACTIVITY.EMAIL_TITLE.DROPPED_V2',
  'attachment downloaded': 'ACTIVITY.EMAIL_TITLE.DOWNLOADED_V2',
  failed: 'ACTIVITY.EMAIL_TITLE.FAILED_V2',
  forwarded: 'ACTIVITY.EMAIL_TITLE.FORWARDED_V2',
  opened: 'ACTIVITY.EMAIL_TITLE.OPENED_V2',
  [APPLE_MPP_OPENED_STATUS]: 'ACTIVITY.EMAIL_TITLE.OPENED_APPLE_MPP_V2',
  processed: 'ACTIVITY.EMAIL_TITLE.PROCESSED_V2',
  received: 'ACTIVITY.EMAIL_TITLE.RECEIVED_V2',
  'received reply': 'ACTIVITY.EMAIL_TITLE.RECEIVED_REPLY_V2',
  replied: 'ACTIVITY.EMAIL_TITLE.REPLY_V2',
  resubscribed: 'ACTIVITY.EMAIL_TITLE.RESUBSCRIBED_V2',
  send: 'ACTIVITY.EMAIL_TITLE.SEND_V2',
  sent: 'ACTIVITY.EMAIL_TITLE.SENT_V2',
  spamreport: 'ACTIVITY.EMAIL_TITLE.SPAM_V2',
  unsubscribed: 'ACTIVITY.EMAIL_TITLE.UNSUBSCRIBED_V2',
};

export const EMAIL_PREPOSITION_BY = 'ACTIVITY.EMAIL_TITLE.EMAIL_BY';
export const EMAIL_PREPOSITION_TO = 'ACTIVITY.EMAIL_TITLE.EMAIL_TO';

// Which preposition joins the email title to the contact name: "by" when the
// contact performed the action (opened/clicked/unsubscribed…), "to" when the
// email was directed at them (sent/delivered/bounced…). Every status in
// EMAIL_TITLE_KEYS_V2 must appear here — the spec enforces it, so a newly added
// status fails CI rather than silently defaulting to the wrong preposition.
export const EMAIL_TITLE_PREPOSITIONS_V2: Record<string, string> = {
  bounced: EMAIL_PREPOSITION_TO,
  clicked: EMAIL_PREPOSITION_BY,
  deferred: EMAIL_PREPOSITION_TO,
  delivered: EMAIL_PREPOSITION_TO,
  dropped: EMAIL_PREPOSITION_TO,
  'attachment downloaded': EMAIL_PREPOSITION_BY,
  failed: EMAIL_PREPOSITION_TO,
  forwarded: EMAIL_PREPOSITION_TO,
  opened: EMAIL_PREPOSITION_BY,
  [APPLE_MPP_OPENED_STATUS]: EMAIL_PREPOSITION_BY,
  processed: EMAIL_PREPOSITION_TO,
  received: EMAIL_PREPOSITION_BY,
  'received reply': EMAIL_PREPOSITION_BY,
  replied: EMAIL_PREPOSITION_BY,
  resubscribed: EMAIL_PREPOSITION_BY,
  send: EMAIL_PREPOSITION_TO,
  sent: EMAIL_PREPOSITION_TO,
  spamreport: EMAIL_PREPOSITION_BY,
  unsubscribed: EMAIL_PREPOSITION_BY,
};

// Falls back to direction for statuses not in the map (raw/unknown values):
// inbound is always a contact action ("by"), everything else defaults to "to".
export function getEmailPreposition(status: string, emailDirection: string | undefined): string {
  return (
    EMAIL_TITLE_PREPOSITIONS_V2[status?.toLowerCase().trim()] ??
    (emailDirection === 'inbound' ? EMAIL_PREPOSITION_BY : EMAIL_PREPOSITION_TO)
  );
}

export const EMAIL_TITLE_KEYS_V1: Record<string, string> = {
  bounced: 'ACTIVITY.EMAIL_TITLE.BOUNCED',
  clicked: 'ACTIVITY.EMAIL_TITLE.CLICKED',
  deferred: 'ACTIVITY.EMAIL_TITLE.DEFERRED',
  delivered: 'ACTIVITY.EMAIL_TITLE.DELIVERED',
  dropped: 'ACTIVITY.EMAIL_TITLE.DROPPED',
  downloaded: 'ACTIVITY.EMAIL_TITLE.DOWNLOADED',
  failed: 'ACTIVITY.EMAIL_TITLE.FAILED',
  opened: 'ACTIVITY.EMAIL_TITLE.OPENED',
  processed: 'ACTIVITY.EMAIL_TITLE.PROCESSED',
  received: 'ACTIVITY.EMAIL_TITLE.RECEIVED',
  'received reply': 'ACTIVITY.EMAIL_TITLE.RECEIVED_REPLY',
  resubscribed: 'ACTIVITY.EMAIL_TITLE.RESUBSCRIBED',
  send: 'ACTIVITY.EMAIL_TITLE.SEND',
  sent: 'ACTIVITY.EMAIL_TITLE.SENT',
  spamreport: 'ACTIVITY.EMAIL_TITLE.SPAM',
  unsubscribed: 'ACTIVITY.EMAIL_TITLE.UNSUBSCRIBED',
};

export const EMAIL_STATUS_KEYS_V1: Record<string, string> = {
  bounced: 'ACTIVITY.EMAIL_STATUS.BOUNCED',
  clicked: 'ACTIVITY.EMAIL_STATUS.CLICKED',
  deferred: 'ACTIVITY.EMAIL_STATUS.DEFERRED',
  delivered: 'ACTIVITY.EMAIL_STATUS.DELIVERED',
  dropped: 'ACTIVITY.EMAIL_STATUS.DROPPED',
  downloaded: 'ACTIVITY.EMAIL_STATUS.DOWNLOADED',
  failed: 'ACTIVITY.EMAIL_STATUS.FAILED',
  opened: 'ACTIVITY.EMAIL_STATUS.OPENED',
  processed: 'ACTIVITY.EMAIL_STATUS.PROCESSED',
  received: 'ACTIVITY.EMAIL_STATUS.RECEIVED',
  reply: 'ACTIVITY.EMAIL_STATUS.REPLY',
  resubscribed: 'ACTIVITY.EMAIL_STATUS.RESUBSCRIBED',
  send: 'ACTIVITY.EMAIL_STATUS.SEND',
  sent: 'ACTIVITY.EMAIL_STATUS.SENT',
  spamreport: 'ACTIVITY.EMAIL_STATUS.SPAM',
  unsubscribed: 'ACTIVITY.EMAIL_STATUS.UNSUBSCRIBED',
};

export function getEmailTranslationKey(status: string, map: Record<string, string>): string {
  if (!status) {
    return '';
  }
  // Unmapped statuses fall through as the raw value so the UI shows the literal
  // status rather than an empty cell or an unresolved translation key.
  return map[status.toLowerCase().trim()] ?? status;
}
